import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Reveal } from "@/components/Reveal";
import { Play } from "lucide-react";

/* Same-origin /api paths: the ingress routes them to the backend, so the
   <track> subtitle fetches need no CORS handling at all. */
const MEDIA_BASE = "/api/media/vision";

/* =============================================================== */
/*  THE AIMA VISION — centered 16:9 video, native controls         */
/* =============================================================== */
export const VisionSection = () => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "en").slice(0, 2) === "ro" ? "ro" : "en";
    const [unavailable, setUnavailable] = useState(false);
    const videoSrc = `${MEDIA_BASE}/${lang}`;

    // Switching language swaps the video source — give the new source a fresh error state.
    useEffect(() => setUnavailable(false), [lang]);

    return (
        <section
            id="vision"
            data-testid="vision-section"
            className="relative section-y overflow-hidden"
        >
            <div className="relative container-x max-w-[1400px] mx-auto">
                <div className="text-center max-w-3xl mx-auto">
                    <Reveal>
                        <div className="eyebrow" data-testid="vision-eyebrow">
                            {t("vision.eyebrow")}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="h-display mt-5 text-4xl md:text-5xl lg:text-[64px] text-white leading-[0.95]"
                            data-testid="vision-headline"
                        >
                            {t("vision.h2a")}{" "}
                            <span className="text-gradient-accent">{t("vision.h2b")}</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p
                            className="mt-6 font-plex text-white/80 text-lg md:text-[19px] leading-[1.55]"
                            data-testid="vision-description"
                        >
                            {t("vision.description")}
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.25} y={32} className="mt-14 md:mt-16 max-w-5xl mx-auto">
                    <div className="relative aspect-video border border-white/[0.08] bg-black overflow-hidden">
                        {unavailable ? (
                            <div
                                className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                                data-testid="vision-video-placeholder"
                            >
                                <span className="inline-flex items-center justify-center w-14 h-14 border border-white/10 bg-white/[0.02]">
                                    <Play className="w-5 h-5 text-white/50" strokeWidth={1.5} />
                                </span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/40">
                                    {t("vision.coming_soon")}
                                </span>
                            </div>
                        ) : (
                            <video
                                controls
                                preload="none"
                                playsInline
                                crossOrigin="anonymous"
                                poster="/media/vision-poster.jpg"
                                src={videoSrc}
                                onError={() => setUnavailable(true)}
                                data-testid="vision-video"
                                className="absolute inset-0 w-full h-full object-contain bg-black"
                            >
                                <track
                                    kind="subtitles"
                                    srcLang="en"
                                    label="English"
                                    src={`${MEDIA_BASE}/subtitles/en.vtt`}
                                    default={lang === "en"}
                                    data-testid="vision-track-en"
                                />
                                <track
                                    kind="subtitles"
                                    srcLang="ro"
                                    label="Română"
                                    src={`${MEDIA_BASE}/subtitles/ro.vtt`}
                                    default={lang === "ro"}
                                    data-testid="vision-track-ro"
                                />
                            </video>
                        )}
                    </div>
                </Reveal>

            
            </div>
        </section>
    );
};

export default VisionSection;
